import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { TherapistEventModel } from "../../models/api/TherapistEventModel";
import { TherapistEventFindResultModel } from "../../models/api/TherapistEventFindResultModel";

export abstract class TherapistEventBaseService extends BaseService {

    private urlPart: string = 'TherapistEvent';

    constructor(private http: HttpClient, private router: Router, private auth: AuthService) {
        super(http, router, auth);
        this.apiSegmentUrl = this.urlPart;
    };

    /// <summary>
    /// FIND: Retrieves a list of TherapistEvent records from the database
    /// /TherapistEvent
    /// </summary>
    public findTherapistEvent(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = ""): Observable<TherapistEventFindResultModel>
    {
        // Find TherapistEvent Records

        const url = `${super.getBaseUrl()}?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}`;

        if (!environment.production)
            console.log("Find TherapistEvent: Calling " + url);

        return this.http.get<TherapistEventFindResultModel>(url)
            .pipe(
                map(next => {
                    let o: TherapistEventFindResultModel = Object.assign(new TherapistEventFindResultModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// GET: Retrieves a single TherapistEvent record from the database
    /// /TherapistEvent/{therapisteventid}
    /// </summary>
    /// <param name="therapisteventid"></param>
    /// <returns></returns>
    public getTherapistEvent(therapisteventid: number): Observable<TherapistEventModel>
    {

        // Get Single TherapistEvent Record

        const url = `${super.getBaseUrl()}/${therapisteventid}`;

        if (!environment.production)
            console.log("Get TherapistEvent: Calling " + url);

        return this.http.get<TherapistEventModel>(url)
            .pipe(
                map(next => {
                    let o: TherapistEventModel = Object.assign(new TherapistEventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// POST: Adds a new TherapistEvent record to the database
    /// /TherapistEvent/{therapisteventid}
    /// </summary>
    /// <param name="therapisteventid"></param>
    /// <returns></returns>
    public createTherapistEvent(newRecord: TherapistEventModel): Observable<TherapistEventModel>
    {
        // Add Single TherapistEvent Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Create TherapistEvent:Calling " + url);

        return this.http.post<TherapistEventModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: TherapistEventModel = Object.assign(new TherapistEventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing TherapistEvent record in the database
    /// /TherapistEvent/{therapisteventid}
    /// </summary>
    /// <param name="therapisteventid"></param>
    /// <returns></returns>
    public updateTherapistEvent(therapisteventid: number, updatedRecord: TherapistEventModel): Observable<TherapistEventModel>
    {
        // Update Single TherapistEvent Record

        const url = `${super.getBaseUrl()}/${therapisteventid}`;

        if (!environment.production)
            console.log("Update TherapistEvent: Calling " + url);

        return this.http.put<TherapistEventModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: TherapistEventModel = Object.assign(new TherapistEventModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateTherapistEventRecord(updatedRecord: TherapistEventModel): Observable<TherapistEventModel>
    {
        return this.updateTherapistEvent(updatedRecord.therapistEventID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing TherapistEvent record from the database
    /// /TherapistEvent/{therapisteventid}
    /// </summary>
    /// <param name="therapisteventid"></param>
    /// <returns></returns>
    public deleteTherapistEvent(therapisteventid: number): Observable<ApiResult>
    {
        // Delete Single TherapistEvent Record

        const url = `${super.getBaseUrl()}/${therapisteventid}`;

        if (!environment.production)
            console.log("Delete TherapistEvent: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteTherapistEventRecord(deleteRecord: TherapistEventModel): Observable<ApiResult>
    {
        // Delete Single TherapistEvent Record
        return this.deleteTherapistEvent(deleteRecord.therapistEventID);
    }

}
